import * as ProductDetailsActions from './../actions/product-details.actions';
import * as SmoothAccessActions from './../actions/smooth-access.actions';
import {
  DeepLinkProductWrapper,
  Option,
  PdaResponseModel,
  Subscription,
  TechnicalError,
} from '@dch/products-business-logic';

import { cloneDeep } from 'lodash';

export interface ProductDetailsState {
  isPending: boolean;
  isPendingAvailableOptions: boolean;
  isPendingAvailableSettings: boolean;
  isPendingAvailableSubscriptions: boolean;
  error: TechnicalError;
  errorAvailableOptions: TechnicalError;
  errorAvailableSettings: TechnicalError;
  errorAvailableSubscriptions: TechnicalError;
  selectedProductDetails: PdaResponseModel;
  deepLinkProductWrapper: DeepLinkProductWrapper;
  optionDetails: any;
  mandateDeleted: boolean;
  availableOptions: Array<Option>;
  availableOptionsLoaded: boolean;
  availableSettings: Array<Option>;
  availableSettingsLoaded: boolean;
  availableSubscriptions: Array<Subscription>;
  availableSubscriptionsLoaded: boolean;
  isUnavailableSubscriptionFlexPackLink?: boolean;
}

export const initialState: ProductDetailsState = {
  isPending: false,
  isPendingAvailableOptions: false,
  isPendingAvailableSettings: false,
  isPendingAvailableSubscriptions: false,
  error: null,
  errorAvailableOptions: null,
  errorAvailableSettings: null,
  errorAvailableSubscriptions: null,
  selectedProductDetails: null,
  optionDetails: null,
  deepLinkProductWrapper: null,
  mandateDeleted: null,
  availableOptions: null,
  availableOptionsLoaded: false,
  availableSettings: null,
  availableSettingsLoaded: false,
  availableSubscriptions: null,
  availableSubscriptionsLoaded: false,
  isUnavailableSubscriptionFlexPackLink: false,
};

export const getTechnicalError = (state: ProductDetailsState) => state.error;
export const getAvailableOptions = (state: ProductDetailsState) =>
  state.availableOptions;
export const getOptionDetails = (state: ProductDetailsState) =>
  state.optionDetails;
export const getAvailableOptionsError = (state: ProductDetailsState) =>
  state.errorAvailableOptions;
export const getAvailableOptionsLoaded = (state: ProductDetailsState) =>
  state.availableOptionsLoaded ||
  (state.availableOptions && state.availableOptions.length > 0);
export const getAvailableSettings = (state: ProductDetailsState) =>
  state.availableSettings;
export const getAvailableSettingsError = (state: ProductDetailsState) =>
  state.errorAvailableSettings;
export const getAvailableSettingsLoaded = (state: ProductDetailsState) =>
  state.availableSettingsLoaded ||
  (state.availableSettings && state.availableSettings.length > 0);
export const getAvailableSubscriptionsLoaded = (state: ProductDetailsState) =>
  state.availableSubscriptionsLoaded ||
  (state.availableSubscriptions && state.availableSubscriptions.length > 0);
export const getAvailableSubscriptions = (state: ProductDetailsState) => {
  return {
    availableSubscriptions: state.availableSubscriptions,
    isUnavailableSubscriptionFlexPackLink:
      state.isUnavailableSubscriptionFlexPackLink,
  };
};
export const getSelectedProductDetails = (state: ProductDetailsState) => {
  // deep clone to avoid changing original product details in store
  const selectedProductDetails = cloneDeep(state.selectedProductDetails);
  const selectedProduct =
    selectedProductDetails != null &&
    (selectedProductDetails.mobileProductDetails ||
      selectedProductDetails.tvProductDetails ||
      selectedProductDetails.telephonyProductDetails ||
      selectedProductDetails.internetProductDetails);
  if (selectedProduct) {
    const availableOptions = getAvailableOptions(state);
    selectedProduct.options = (selectedProduct.options || [])
      .concat(availableOptions || [])
      .sort((a, b) => {
        // sort options by rank (natural order in supportedOptions JSON)
        if (a.rank < b.rank) {
          return -1;
        }
      });

    const availableSettings = getAvailableSettings(state);
    selectedProduct.settings = (selectedProduct.settings || [])
      .concat(availableSettings || [])
      .sort((a, b) => {
        // sort options by rank (natural order in supportedOptions JSON)
        if (a.rank < b.rank) {
          return -1;
        }
      });
  }
  return selectedProductDetails;
};

export const getPending = (state: ProductDetailsState) => state.isPending;
export const getMandateDeleted = (state: ProductDetailsState) =>
  state.mandateDeleted;
export const getDeepLinkProductWrapper = (state: ProductDetailsState) =>
  state.deepLinkProductWrapper;
export const getAllDeepLinkProducts = (state: ProductDetailsState) => {
  if (!state.deepLinkProductWrapper) {
    return null;
  } else {
    return (
      state.deepLinkProductWrapper.mobileProductList ||
      []
        .concat(state.deepLinkProductWrapper.internetProductList || [])
        .concat(state.deepLinkProductWrapper.tvProductList || [])
    );
  }
};
export const getDisneyPlusProductDetails = (state: ProductDetailsState) =>
  state.selectedProductDetails;

export function reducer(
  state: ProductDetailsState = initialState,
  action: ProductDetailsActions.Actions,
): ProductDetailsState {
  switch (action.type) {
    case ProductDetailsActions.GET_PRODUCT_DETAILS:
      const product =
        state.selectedProductDetails != null &&
        (state.selectedProductDetails.mobileProductDetails ||
          state.selectedProductDetails.tvProductDetails ||
          state.selectedProductDetails.internetProductDetails ||
          state.selectedProductDetails.telephonyProductDetails ||
          state.selectedProductDetails.ePressProductDetails ||
          (state.selectedProductDetails as any).digitalisProductDetails);
      if (product && product.accessNumber !== action.payload.serviceId) {
        return {
          ...state,
          isPending: true,
          selectedProductDetails: null,
          availableOptions: null,
          availableSettings: null,
          error: undefined,
        };
      } else {
        return {
          ...state,
          isPending: true,
          error: undefined,
        };
      }
    case ProductDetailsActions.GET_PRODUCT_DETAILS_SUCCESS:
      return {
        ...state,
        isPending: false,
        selectedProductDetails: action.payload,
      };
    case ProductDetailsActions.GET_PRODUCT_DETAILS_SUCCESS_NO_CHANGE:
      return {
        ...state,
        isPending: false,
      };
    case ProductDetailsActions.GET_PRODUCT_DETAILS_FAILED:
      return {
        ...state,
        error: action.payload || {},
        isPending: false,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_OPTIONS:
      return {
        ...state,
        isPendingAvailableOptions: true,
        availableOptionsLoaded: false,
        errorAvailableOptions: null,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_OPTIONS_SUCCESS:
      const selectedProduct =
        state.selectedProductDetails != null &&
        (state.selectedProductDetails.mobileProductDetails ||
          state.selectedProductDetails.tvProductDetails ||
          state.selectedProductDetails.telephonyProductDetails ||
          state.selectedProductDetails.internetProductDetails);
      const productWithAvailableOptions =
        action.payload != null &&
        (action.payload.mobileProductDetails ||
          action.payload.tvProductDetails ||
          action.payload.telephonyProductDetails ||
          action.payload.internetProductDetails);
      if (
        selectedProduct.accessNumber ===
        productWithAvailableOptions.accessNumber
      ) {
        return {
          ...state,
          isPendingAvailableOptions: false,
          availableOptions: productWithAvailableOptions.options,
          availableOptionsLoaded: true,
        };
      } else {
        return {
          ...state,
          isPendingAvailableOptions: false,
          availableOptions: null,
          availableOptionsLoaded: false,
        };
      }
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_OPTIONS_SUCCESS_NO_CHANGE:
      return {
        ...state,
        isPendingAvailableOptions: false,
        availableOptionsLoaded: true,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_OPTIONS_FAILED:
      return {
        ...state,
        errorAvailableOptions: action.payload || {},
        isPendingAvailableOptions: false,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_SETTINGS:
      return {
        ...state,
        isPendingAvailableSettings: true,
        availableSettingsLoaded: false,
        errorAvailableSettings: null,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_SETTINGS_SUCCESS:
      const selectedProduct1 =
        state.selectedProductDetails != null &&
        (state.selectedProductDetails.mobileProductDetails ||
          state.selectedProductDetails.tvProductDetails ||
          state.selectedProductDetails.internetProductDetails);
      const productWithAvailableSettings =
        action.payload != null &&
        (action.payload.mobileProductDetails ||
          action.payload.tvProductDetails ||
          action.payload.internetProductDetails);
      if (
        selectedProduct1.accessNumber ===
        productWithAvailableSettings.accessNumber
      ) {
        return {
          ...state,
          isPendingAvailableSettings: false,
          availableSettings: productWithAvailableSettings.settings,
          availableSettingsLoaded: true,
        };
      } else {
        return {
          ...state,
          isPendingAvailableSettings: false,
          availableSettings: null,
          availableSettingsLoaded: false,
        };
      }
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_SETTINGS_SUCCESS_NO_CHANGE:
      return {
        ...state,
        isPendingAvailableSettings: false,
        availableSettingsLoaded: true,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_SETTINGS_FAILED:
      return {
        ...state,
        errorAvailableSettings: action.payload || {},
        isPendingAvailableSettings: false,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_SUBSCRIPTIONS:
      return {
        ...state,
        isPendingAvailableSubscriptions: true,
        availableSubscriptionsLoaded: false,
        errorAvailableSubscriptions: null,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_SUBSCRIPTIONS_SUCCESS:
      const selectedProductMobile =
        state.selectedProductDetails != null &&
        (state.selectedProductDetails.mobileProductDetails ||
          state.selectedProductDetails.tvProductDetails ||
          state.selectedProductDetails.internetProductDetails);
      const productWithAvailableSubscriptions =
        action.payload != null &&
        (action.payload.mobileProductDetails ||
          action.payload.tvProductDetails ||
          action.payload.internetProductDetails);
      if (
        selectedProductMobile.accessNumber ===
        productWithAvailableSubscriptions.accessNumber
      ) {
        return {
          ...state,
          isPendingAvailableSubscriptions: false,
          availableSubscriptions:
            productWithAvailableSubscriptions.availableSubscriptions,
          isUnavailableSubscriptionFlexPackLink:
            productWithAvailableSubscriptions.isUnavailableSubscriptionFlexPackLink,
          availableSubscriptionsLoaded: true,
        };
      } else {
        return {
          ...state,
          isPendingAvailableSubscriptions: false,
          availableSubscriptions: null,
          availableSubscriptionsLoaded: false,
        };
      }
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_SUBSCRIPTIONS_SUCCESS_NO_CHANGE:
      return {
        ...state,
        isPendingAvailableSubscriptions: false,
        availableSubscriptionsLoaded: true,
      };
    case ProductDetailsActions.GET_PRODUCT_AVAILABLE_SUBSCRIPTIONS_FAILED:
      return {
        ...state,
        errorAvailableSubscriptions: action.payload || {},
        isPendingAvailableSubscriptions: false,
      };
    case ProductDetailsActions.CLEAR_DELETE_MANDATE_FLAG:
      return {
        ...state,
        mandateDeleted: null,
      };
    case ProductDetailsActions.DELETE_MANDATE_SUCCESS:
      const selectedProductDetails = getSelectedProductDetails(state);
      selectedProductDetails.mobileProductDetails.options.map((value) => {
        if (value.optionCodeForDescription === action.payload.mpcUuid) {
          value.mandateResource.mandates =
            value.mandateResource.mandates.filter(
              (mandate) =>
                mandate.mandateId !== action.payload.mandate.mandateId,
            );
        }
      });
      return {
        ...state,
        selectedProductDetails,
        mandateDeleted: true,
      };
    case ProductDetailsActions.DELETE_MANDATE_ERROR:
      return {
        ...state,
        mandateDeleted: false,
      };
    case ProductDetailsActions.GET_DEEP_LINK_OPTION_DETAILS:
      return {
        ...state,
        deepLinkProductWrapper: null,
        isPending: true,
      };
    case ProductDetailsActions.GET_DEEP_LINK_OPTION_DETAILS_SUCCESS:
      return {
        ...state,
        deepLinkProductWrapper: action.payload,
        isPending: false,
      };
    case ProductDetailsActions.GET_DEEP_LINK_OPTION_DETAILS_SUCCESS_NO_CHANGE:
      return {
        ...state,
        isPending: false,
      };
    case ProductDetailsActions.GET_DEEP_LINK_OPTION_DETAILS_FAILED:
      return {
        ...state,
        error: action.payload || {},
        isPending: false,
      };
    case ProductDetailsActions.GET_OPTION_DETAILS:
      return {
        ...state,
        optionDetails: null,
        isPending: true,
      };
    case ProductDetailsActions.GET_OPTION_DETAILS_SUCCESS:
      return {
        ...state,
        optionDetails: action.payload,
        isPending: false,
      };
    case ProductDetailsActions.GET_OPTION_DETAILS_SUCCESS_NO_CHANGE:
      return {
        ...state,
        isPending: false,
      };
    case ProductDetailsActions.GET_OPTION_DETAILS_FAILED:
      return {
        ...state,
        error: action.payload || {},
        isPending: false,
      };
    case SmoothAccessActions.GET_DISNEY_PLUS_DETAILS:
      return {
        ...state,
        isPending: true,
        selectedProductDetails: null,
        availableOptions: null,
        availableSettings: null,
        error: undefined,
      };
    case SmoothAccessActions.GET_DISNEY_PLUS_DETAILS_SUCCESS:
      return {
        ...state,
        isPending: false,
        selectedProductDetails: action.payload,
      };
    case SmoothAccessActions.GET_DISNEY_PLUS_DETAILS_FAILED:
      return {
        ...state,
        error: action.payload || {},
        isPending: false,
      };
    case ProductDetailsActions.GET_EBU_PRODUCT_DETAILS:
      const ebuProduct =
        state.selectedProductDetails != null &&
        state.selectedProductDetails.mobileProductDetails;
      if (ebuProduct && ebuProduct.accessNumber !== action.payload.serviceId) {
        return {
          ...state,
          isPending: true,
          selectedProductDetails: null,
          availableOptions: null,
          availableSettings: null,
          error: undefined,
          isPendingAvailableOptions: true,
          availableOptionsLoaded: false,
          errorAvailableOptions: null,
        };
      } else {
        return {
          ...state,
          isPending: true,
          error: undefined,
          isPendingAvailableOptions: true,
          availableOptionsLoaded: false,
          errorAvailableOptions: null,
        };
      }
    case ProductDetailsActions.GET_EBU_PRODUCT_DETAILS_FAILED:
      return {
        ...state,
        error: action.payload || {},
        isPending: false,
      };
    case ProductDetailsActions.GET_EBU_PRODUCT_DETAILS_SUCCESS:
      return {
        ...state,
        isPending: false,
        selectedProductDetails: action.payload,
        isPendingAvailableOptions: false,
        availableOptions: action.payload.options,
        availableOptionsLoaded: true,
      };
    case ProductDetailsActions.GET_EBU_PRODUCT_DETAILS_SUCCESS_NO_CHANGE:
      return {
        ...state,
        isPending: false,
      };
    case ProductDetailsActions.UPDATE_EBU_ACTIVE_OPTIONS_SUCCESS:
      return {
        ...state,
        selectedProductDetails: action.payload,
        availableOptions: action.payload.options,
      };
    default:
      return state;
  }
}
